import { useState, useRef, useEffect } from 'react';
import profilePhoto from './assets/Personal_Pic_20240814.jpg';

import cyberone1_img0 from './assets/cyberone1/cyberone1_0.jpg';
import cyberone1_img1 from './assets/cyberone1/cyberone1_1.jpg';

import cyberdog2_img0 from './assets/cyberdog2/cyberdog2_0.png';
import cyberdog2_img1 from './assets/cyberdog2/cyberdog2_1.jpg';
import cyberdog2_img2 from './assets/cyberdog2/cyberdog2_2.jpg';
import cyberdog2_img3 from './assets/cyberdog2/cyberdog2_3.gif';

import archi_phone_img0 from './assets/architecture_smartphone/CAD_explosure_example.png';
import archi_phone_img1 from './assets/architecture_smartphone/honor20_side_fingerprint.png';
import archi_phone_img2 from './assets/architecture_smartphone/nova5_official.png'

import fea_phone_img0 from './assets/sim_smartphone/FEA_phone_drop.png';
import fea_phone_img1 from './assets/sim_smartphone/FEA_phone_bending.png';
import fea_phone_img2 from './assets/sim_smartphone/FEA_phone_twisting.png';

import ddavinci_img0 from './assets/DdaVinci/discount_daVinci_0.jpg';
import ddavinci_img1 from './assets/DdaVinci/discount_daVinci_1.jpg';
import ddavinci_img2 from './assets/DdaVinci/discount_daVinci_2.png';

import foot_img0 from './assets/research_foot_FEA/foot_whole_model.png';
import foot_img1 from './assets/research_foot_FEA/foot_CT.png';
import foot_img2 from './assets/research_foot_FEA/foot_inside_model.png';
import foot_img3 from './assets/research_foot_FEA/foot_result.png';
import foot_img4 from './assets/research_foot_FEA/foot_patient_comparison.png';

import handcontroller6_img0 from './assets/hand_control_6/handcontrol-6-v1_0.png';
import handcontroller6_img1 from './assets/hand_control_6/handcontrol-6-v1_1.png';
import handcontroller6_img2 from './assets/hand_control_6/handcontrol-6-v2_1.png';

import eyemech_v2 from './assets/EyeMech_upgrade/EyeMech_v2.jpg';


const Icons = {
  ChevronDown: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  ExternalLink: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  )
};

// Helper: parse "**bold**" in a string into <strong>
function renderWithBold(text) {
  const parts = text.split(/(\*\*[^\*]+\*\*)/g).filter(Boolean);
  return parts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={idx} className="font-bold">{part.slice(2, -2)}</strong>;
    }
    return <span key={idx}>{part}</span>;
  });
}

export default function PersonalWebsite() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectSlideIndex, setProjectSlideIndex] = useState(0);

  const [selectedEducation, setSelectedEducation] = useState(null);
  const [selectedWork, setSelectedWork] = useState(null);
  const [activeSection, setActiveSection] = useState('home');
  const [scrollY, setScrollY] = useState(0);

  const homeRef = useRef(null);
  const projectsRef = useRef(null);
  const experienceRef = useRef(null);
  const contactRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const updateSection = () => {
      if (!projectsRef.current || !experienceRef.current || !contactRef.current) return;
      const pos = window.scrollY + 100;
      const projTop = projectsRef.current.offsetTop;
      const expTop = experienceRef.current.offsetTop;
      const contactTop = contactRef.current.offsetTop;
      if (pos >= contactTop) setActiveSection('contact');
      else if (pos >= expTop) setActiveSection('experience');
      else if (pos >= projTop) setActiveSection('projects');
      else setActiveSection('home');
    };
    window.addEventListener('scroll', updateSection);
    return () => window.removeEventListener('scroll', updateSection);
  }, []);

  const scrollToSection = (section) => {
    const refs = { home: homeRef, projects: projectsRef, experience: experienceRef, contact: contactRef };
    refs[section]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(section);
  };

  const threshold = 200;
  const minScale = 0.4;
  const scale = scrollY < threshold ? 1 - (scrollY / threshold) * (1 - minScale) : minScale;
  const showAvatar = scrollY > threshold;

  const personalInfo = {
    name: 'Xinwen Xu',
    title: 'Simulation & Robotics Engineer',
    location: 'San Francisco Bay Area, CA',
    intro: [
      '7+ years of combined experience in advanced robotics and high volume hardware.',
      'Expert in transitioning complex systems from concept to production using simulation pipelines and reliability validation.',
      'Currently developing LLM-driven scenario generation workflows for robotic learning at CMU. '
    ],
    email: 'flynews.xu@gmail.com',
    linkedin: 'https://www.linkedin.com/in/xinwen-xu-077671112/',
    education: [
      {
        id: 1,
        degree: 'Master of Science',
        institution: 'Carnegie Mellon University',
        major: 'Mechanical Engineering',
        period: '2024.08 - 2026.05',
        focus: 'Advanced robotics simulation and control',
        courses: 'Modern Control Theory, Robot Dynamics and Analysis, Mechanics of Manipulation, Optimal Control and Reinforcement Learning, etc.',
        thesis: 'TBD'
      },
      {
        id: 2,
        degree: 'Master of Science',
        institution: 'Peking University',
        major: 'Biomechanics and Medical Engineering',
        period: '2013.09 - 2016.07',
        focus: 'Mechanical modeling and analysis of biological organs and tissues',
        courses: 'Continuum Mechanics, Structural analysis and numerical computation, Computational geometry, Advanced Operations Research, etc.',
        thesis: 'Finite Element Modeling of the Whole Foot and the Application in Diabetic Foot Disease'
      },
      {
        id: 3,
        degree: 'Bachelor of Engineering',
        institution: 'Peking University',
        major: 'Structural Analysis',
        period: '2009.09 - 2013.07',
        focus: 'Mechanical design and analysis of structures',
        courses: 'Structural Mechanics, Theoretical Mechanics, Engineering Elasticity, Engineering Fluid, Mechanics, VC Programming, Mechanical Graphing, etc. ',
        thesis: 'The Finite Element Analysis of Dental Casts with Different Filling Materials'
      }
    ],
    workExperience: [
      {
        id: 1,
        role: 'Simulation Team Lead',
        company: 'Robotics Lab, Xiaomi Corporation',
        period: '2021.10 - 2024.08',
        bullets: [
          'Led a 13-member hardware simulation team for legged robots, supporting hardware architecture design and reliability enhancements.',
          'Construct simulation technologies for impact, vibration, fatigue, optics, dynamic optimization, joint power consumption analysis, etc.',
          'Developed frameworks, metrics and standards for reliability simulation and testing.'
        ],
        details: [
          'Led a 13-member hardware simulation technology team, providing support for hardware architecture design and reliability enhancements in quadruped and biped projects.',
          'Construct simulation technologies for impact, vibration, fatigue, optics, dynamic optimization, joint power consumption analysis, etc., driving improvements in the consistency between simulation and testing.',
          'Spearheaded multiple initiatives to address reliability issues, establishing standards for simulation and testing in the field of legged robot technology.'
        ],
        honors: [
          {
            id: 1,
            title: 'Xiaomi Million USD Award Third Prize',
            year: '2024',
            name: 'Feeling Hand',
            description: 'Developed a mature humanoid robotic hand, demonstrating advanced dexterity and reliability.',
          },
          {
            id: 2,
            title: 'Xiaomi Young Engineer Award',
            year: '2023',
            name: 'Outstanding Young Engineer',
            description: 'Recognized for innovative contributions in hardware simulation and design.',
          }
        ]
      },
      {
        id: 2,
        role: 'Hardware Architecture\nEngineer',
        company: 'Consumer Business Group,\nHuawei Technologies Co.',
        period: '2018.08 - 2020.07',
        bullets: [
          'Undertook hardware architecture and stack design for 2 mobile phone projects.',
          'Addressed simulation and testing issues such as LCD delamination and light leakage.'
        ],
        details: [
          'Undertook hardware architecture and stack design for 2 mobile phone projects including Nova pre-research and the Honor sub-series.',
          'Addressed simulation and testing issues such as LCD delamination and light leakage, by providing comprehensive architectural design solutions and driving their implementation.'
        ]
      },
      {
        id: 3,
        role: 'Mechanical Engineer',
        company: 'Consumer Business Group,\nHuawei Technologies Co.',
        period: '2016.07 - 2018.08',
        bullets: [
          'Managed mechanical simulation and reliability testing for 6 mass-produced smartphone projects.',
          'Performed FEA analysis and validated simulation models.'
        ],
        details: [
          'Directed 3D finite element simulations for 6 mass-produced mobile phone projects, and exported the results and optimization recommendations to ensure the smooth market launch of these products.',
          'Collaborated with related fields to raise awareness of simulation and promote the adoption of new methods.'
        ]
      }
    ]
  };

  const projects = {
    work: [
      {
        id: 5,
        title: 'Xiaomi CyberOne 2 (Humanoid Robot)',
        period: '2022.10 - 2024.08',
        mainRole: 'Simulation&Test Team Lead',
        description: 'Led simulation and testing protocols to optimize reliability and accelerate hardware issue resolution.',
        images: [cyberone1_img0, cyberone1_img1],
        images_links: ['https://www.mi.com/cyberone', 'https://robotsguide.com/robots/cyberone'],
        img_statement: 'CyberOne 1 image, for reference only.',
        details: [
          'Developed dynamic, impact, vibration, thermal, and co-simulation capabilities.',
          'Decomposed technical requirements and iteratively optimized hardware issues.',
          'Collaborated with cross-functional teams to validate simulation accuracy.',
          'Established various simulation and testing standards to optimize reliability, enhance product quality, and reduce hardware issues.'
        ]
      },
      {
        id: 4,
        title: 'Xiaomi CyberDog 2 (Quadruped Robot)',
        period: '2021.10 - 2023.06',
        mainRole: 'Simulation&Test Team Lead',
        description: 'Optimized simulation model and established reliability standards for mass production.',
        images: [cyberdog2_img1, cyberdog2_img0, cyberdog2_img3, cyberdog2_img2],
        images_links: ['https://robotsguide.com/robots/cyberdog2', 'https://www.mi.com/cyberdog2', 'https://news.epaperia.com/post/451.html', 'https://robotsguide.com/robots/cyberdog2'],
        details: [
          'Optimized the quadruped dynamic simulation model to support product design scheme selection and implementation.',
          'Established and validated reliability simulation capabilities for joint rotation, single-leg falls, impact,etc., ensuring the mass production of quadruped projects.', 
          'Setting up reliability simulation and testing standards for Xiaomi quadruped robots.',
          'Led the resolution of various reliability issues, including laser radar, connectors, and front chest strength, etc.'
        ]
      },
      {
        id: 3,
        title: 'Huawei Nova/Honor (Smart Phone)',
        period: '2020.01 - 2021.07',
        mainRole: 'Hardware Architecture Engineer',
        description: 'Hardware architecture design of 2 cellphone projects (Nova/Honor)',
        images: [archi_phone_img0],
        images_links: ['https://www.gemcoder.com/index.php?m=home&c=View&a=index&aid=1569'],
        img_statement: 'Pictures from the internet, for reference only.',
        details: [
          'Communicated with Planning and Industrial Design engineers, provided total solution of hardware architecture design, and evaluated the hardware feasibility of the ID.'
        ]
      },
      {
        id: 2,
        title: 'Huawei Nova/Honor (Smart Phone)',
        period: '2018.09 - 2019.12',
        mainRole: 'Assistant Hardware Architecture Engineer',
        description: 'Addressed simulation and testing issues such as side-fingerprint & AMOLED reliability, and LCD light leakage.',
        images: [archi_phone_img2, archi_phone_img1],
        images_links: ['https://www.xda-developers.com/huawei-nova-5-pro-5i-mediapad-m6-launched/', 'https://www.antutu.com/doc/118663.htm'],
        img_statement: 'Pictures from the internet, for reference only.',
        details: [
          'Solved the liquid-entering problem of Honor Series, which helped saving X million cost.',
          'Launched the combination scheme of fingerprint and side-switch on Honor Series successfully, providing standardize design solution.',
          'Participated in the technical research team of the AMOLED reliability problem of Nova Series, proposed and verified multiple solutions.'
        ]
      },
      {
        id: 1,
        title: 'Huawei Mass-Produced Cellphone Projects',
        period: '2016.07 - 2018.08',
        mainRole: 'Mechanical Engineer',
        description: 'Mechanical simulation (FEA) of 6 mass-produced cellphone projects, ensured the reliability of the products before and after launch.',
        images: [fea_phone_img0, fea_phone_img1, fea_phone_img2],
        images_links: ['https://femtech.cn/iphone6-drop-test/', 'https://www.think-s.com/CAE?article_id=1710', 'https://www.feaworks.org/about/xinwenzixun/2019-01-24-539.html'],
        img_statement: 'Pictures from the internet, for reference only.',
        details: [
          'Mechanical simulation of Nova Series, Mate lite Series and Honor Series during their entire life cycle, keeping these projects in good performance in the reliability simulation/test (such as dropping, bending, twisting, etc.)',
          'Above 85% of the risks were detected and optimized by FE simulation in the design stage.'
        ]
      }
    ],
    school: [
      {
        id: 4,
        title: 'Discount da Vinci (Course Project)',
        period: '2025.01 - 2025.05',
        mainRole: 'Computer Vision',
        description: 'A low-cost, portable robotic system for automated liver biopsy surgery.',
        link: 'https://team3-16778.github.io/',
        images: [ddavinci_img0, ddavinci_img1, ddavinci_img2, 'https://www.youtube.com/embed/ax77N28-qOc'],
        images_links: ['https://team3-16778.github.io/', 'https://team3-16778.github.io/', 'https://team3-16778.github.io/', 'https://www.youtube.com/embed/ax77N28-qOc'],
        details: [
          'Functional and cyberphysical architecture design of the robotic system.',
          'Developed the 3D localization algorithm of the computer vision.',
          'Implemented the computer vision system on Jetson Orin Nano.',
          'Coordinated team of 4 to integrate hardware and software.'
        ]
      },
      {
        id: 3,
        title: 'Finite Element Modeling of Human Foot (Research Project)',
        period: '2015.01 - 2015.12',
        mainRole: 'Main Researcher',
        description: 'A finite-element foot model integrating ligaments and muscles, effectively assisting medical analyses such as diabetic foot evaluation.',
        images: [foot_img0, foot_img1, foot_img2, foot_img3, foot_img4],
        details: [
          '**Background & Significance:** The foot plays a critical role in load-bearing and locomotion and is a common site for sports injuries and chronic conditions like diabetic foot.',
          '**Data Sources:** Built from CT scans of a 16-year-old healthy male\'s foot, supplemented by MRI data, anatomical atlases, and clinical expertise.',
          '**Model Composition:** A whole-foot finite-element model including 31 bones, 36 cartilages, 9 muscle groups, skin and soft tissue, 87 ligaments, and the plantar aponeurosis.',
          '**Validation:** Applied realistic standing-equilibrium loads and boundary conditions to verify stress distribution and confirm the model\'s validity.',
          '**Diabetic Foot Simulation:** Altered the biomechanical properties of plantar soft tissue under the metatarsal heads and calcaneus to mimic diabetic foot pathology, using the same loading and boundary conditions.',
          '**Key Finding:** Soft-tissue stiffening beneath the metatarsal heads and calcaneus markedly increases internal plantar stress, thereby elevating ulceration risk in diabetic patients.'
        ]
      }      
    ],
    personal: [
      {
        id: 2,
        title: 'HandController-6',
        period: '2025.05 - 2025.06',
        mainRole: 'Full Stack Developer',
        description: 'An low-cost, ultra-compact, one-handed interface for driving a 6-DoF dexterous robotic hand.',
        link: 'https://youtube.com/shorts/ZLHzDVMeDKk',
        images: [handcontroller6_img0, handcontroller6_img1, 'https://youtube.com/embed/ZLHzDVMeDKk', handcontroller6_img2],
        images_links: ['', '', 'https://youtube.com/embed/ZLHzDVMeDKk', ''],
        details: [
          'An low-cost, ultra-compact, one-handed interface for driving a 6-DoF dexterous robotic hand.',
          '**Next Version (V2):** More compact, integrated with a IMU.'
        ]
      },
      {
        id: 1,
        title: 'Updated Version of EyeMech',
        period: '2025.04 - 2025.05',
        mainRole: 'Full Stack Developer',
        description: 'Added Camera and Control Interface to the original open-source EyeMech project.',
        images: [eyemech_v2, "https://www.youtube.com/embed/Sw0JpN0visc", "https://www.youtube.com/embed/BljF-0C3-1Y"],
        images_links: ["", "https://www.youtube.com/embed/Sw0JpN0visc", "https://www.youtube.com/embed/BljF-0C3-1Y"],
        details: [
          'An 8MP CSI camera was embedded into the left eye, powered by a Jetson Orin Nano, allowing EyeMech to “see” its external environment.',
          'EyeMech Control Interface is a desktop application that provides an intuitive way to control a 3D eyeball mechanism using mouse interactions. '
        ]
      }      
    ]
  };

  return (
    <div className="min-h-screen bg-amber-50 text-gray-900">
      <nav className="fixed top-0 left-0 right-0 bg-gray-100 z-50 shadow-md">
        <div className="relative container mx-auto px-10 py-6 flex justify-between items-center">
          <div className="flex items-center">
            {showAvatar && (
              <img
                src={profilePhoto}
                alt="Avatar"
                className="absolute top-5 w-32 h-32 rounded-full z-50"
                style={{
                    transform: showAvatar ? 'translateY(0)' : 'translateY(100%)',
                    transition: 'transform 0.6s ease-out'
                }}
              />
            )}
            <span
              className="font-bold text-4xl"
              style={{
                marginLeft: showAvatar ? '10rem' : '0rem',
                transition: 'margin-left 0.3s ease'
              }}
            >
              Xinwen
            </span>
          </div>
          <div className="flex space-x-8">
            <button onClick={() => scrollToSection('home')} className={`text-lg ${activeSection === 'home' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}>Home</button>
            <button onClick={() => scrollToSection('projects')} className={`text-lg ${activeSection === 'projects' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}>Projects</button>
            <button onClick={() => scrollToSection('experience')} className={`text-lg ${activeSection === 'experience' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}>Experience</button>
            <button onClick={() => scrollToSection('contact')} className={`text-lg ${activeSection === 'contact' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}>Contact</button>
          </div>
        </div>
      </nav>

      <section ref={homeRef} className="scroll-mt-24 pt-24 pb-16 flex items-center min-h-screen">
        <div className="container mx-auto px-6 flex flex-col md:flex-row items-center">
          {/* 左侧：文字 */}
          <div className="md:w-1/2 mb-10 md:mb-0">
            <h1 className="text-5xl font-bold mb-2">Hello, I&apos;m {personalInfo.name}</h1>
            <h2 className="text-3xl font-medium mb-4">{personalInfo.title}</h2>
            <ul className="list-disc pl-8 text-gray-700 mb-8 text-lg space-y-3 leading-relaxed">
              {personalInfo.intro.map((line, idx) => (
                <li key={idx}>{line}</li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3">
              <a href={`mailto:${personalInfo.email}`} className="inline-flex items-center px-4 py-2 bg-gray-300 hover:bg-gray-200 rounded">
                <Icons.ExternalLink />
                <span className="ml-2 text-gray-900">{personalInfo.email}</span>
              </a>
              <a
                href={personalInfo.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-gray-300 hover:bg-gray-200 rounded"
              >
                <Icons.ExternalLink />
                <span className="ml-2 text-gray-900">LinkedIn</span>
              </a>
            </div>
          </div>
          {/* 右侧：肖像 */}
          <div className="md:w-1/2 flex justify-center">
            <div className="w-96 h-96 rounded-full overflow-hidden border-4 border-gray-300 transform transition-transform" style={{ transform: `scale(${scale})` }}>
              <img src={profilePhoto} alt="Avatar" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section ref={projectsRef} className="scroll-mt-24 py-16 bg-amber-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 text-gray-900">Projects</h2>
          {['work', 'school', 'personal'].map(group => (
            <div key={group} className="mb-12">
              <h3 className="text-2xl font-semibold mb-6 text-gray-900">
                {group === 'work' ? 'Work Projects' : group === 'school' ? 'School Projects' : 'Personal Projects'}
              </h3>
              <div className="grid md:grid-cols-2 gap-8">
                {projects[group].slice().sort((a, b) => b.id - a.id).map(p => (
                  <div
                    key={p.id}
                    onClick={() => setSelectedProject(p)}
                    className="bg-gray-50 rounded-lg shadow-md overflow-hidden cursor-pointer transform transition-transform hover:scale-105"
                  >
                    <div className="h-80 overflow-hidden">
                      <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-2 text-gray-900">{p.title}</h3>
                      <div className="flex justify-between text-gray-600 text-lg mb-2">
                        <span>{p.period}</span>
                        <span><strong>Role:</strong> {p.mainRole}</span>
                      </div>
                      <p className="text-gray-700 mb-2">{p.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Project Modal with click zones */}
      {selectedProject && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-20"
          onClick={() => setSelectedProject(null)}
        >
          <div
            className="relative bg-white rounded-lg shadow-lg p-8 max-w-4xl w-full overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold">{selectedProject.title}</h3>
              <button
                onClick={() => setSelectedProject(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            {/* Image viewer with side previews */}
            <div className="relative flex items-center justify-center h-96">
            {(() => {
              // Create media list
              const mediaList = selectedProject.images.map((src, i) => ({
                type: 'image',
                src,
                link: selectedProject.images_links?.[i] || ''
              }))
              // if YouTube link is provided, replace 'image' with 'youtube'
              .map(item => ({
                ...item,
                type: item.link.includes('youtu') ? 'youtube' : item.type,
              }))
              
              // render current
              const current = mediaList[projectSlideIndex]
              return (
                <>
                  {/* Current image */}
                  {current.type === 'image' && (
                    <img
                      src={current.src}
                      alt="current"
                      className="relative w-3/5 h-auto object-cover z-10"
                    />
                  )}
                  {current.type === 'youtube' && (
                    <iframe
                      className="relative w-3/5 h-auto z-10"
                      style={{ aspectRatio: '16/9' }}
                      src={current.link.replace('watch?v=', 'embed/')}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  )}
                  {/*if img_statement is not empty*/}
                  {selectedProject.img_statement && (
                    <span className="absolute bottom-2 right-5 bg-black bg-opacity-50 text-white text-xs px-1 rounded z-20">
                      {selectedProject.img_statement}
                    </span>
                  )}
                  {mediaList.map((item, idx) => (
                    <div key={idx}>
                    {/* Previous image preview */}
                    {idx === (projectSlideIndex - 1 + mediaList.length) % mediaList.length && (
                      <img
                        src={
                          item.type === 'image'
                            ? item.src
                            : `https://img.youtube.com/vi/${
                                item.src.split('/').pop().split('?')[0]
                              }/mqdefault.jpg`
                        }
                        alt="prev"
                        className="absolute left-0 top-1/2 w-2/5 h-auto object-cover opacity-50 transform -translate-y-1/2"
                      />
                    )}
                    {/* Next image preview */}
                    {idx === (projectSlideIndex + 1) % mediaList.length && (
                      <img
                        src={
                          item.type === 'image'
                            ? item.src
                            : `https://img.youtube.com/vi/${
                                item.src.split('/').pop().split('?')[0]
                              }/mqdefault.jpg`
                        }                     
                        alt="next"
                        className="absolute right-0 top-1/2 w-2/5 h-auto object-cover opacity-50 transform -translate-y-1/2"
                      />
                    )}
                  </div>
                ))}

                  {/* Click zones */}
                  <div
                    className="absolute left-0 top-0 h-full w-1/2 cursor-pointer"
                    onClick={() => setProjectSlideIndex(prev => (prev - 1 + selectedProject.images.length) % selectedProject.images.length)}
                  />
                  <div
                    className="absolute right-0 top-0 h-full w-1/2 cursor-pointer"
                    onClick={() => setProjectSlideIndex(prev => (prev + 1) % selectedProject.images.length)}
                  />
                </>
              )
            })()}
            </div>
            {/* Details */}
            <ul className="list-disc list-inside text-gray-700 mt-4">
              {selectedProject.details.map((d, i) => (
                <li key={i} className="mb-2">
                  {renderWithBold(d)}
                </li>
              ))}
            </ul>
            {/* Image source link */}
            {selectedProject.images_links && (
              <p className="mt-4 text-sm text-blue-600">
                <a href={selectedProject.images_links[projectSlideIndex]} target="_blank" rel="noopener noreferrer">
                  View more details on the image source page
                </a>
              </p>
            )}
          </div>
        </div>
      )}

      <section ref={experienceRef} className="scroll-mt-24 py-16 bg-amber-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 text-gray-900">Experience</h2>
          {/* Education cards */}
          <div>
            <h3 className="text-2xl font-semibold mb-6 text-gray-900">Education</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {personalInfo.education.map(ed => (
                <div
                  key={ed.id}
                  onClick={() => setSelectedEducation(ed)}
                  className="bg-white rounded-lg shadow-md overflow-hidden p-6 cursor-pointer transform transition-transform hover:scale-105"
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xl font-medium text-gray-900">{ed.degree}</span>
                    <span className="text-lg text-gray-600">{ed.institution}</span>
                  </div>
                  <p className="text-gray-700 mb-2">{ed.major}</p>
                  <p className="text-gray-600">{ed.period}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Work Experience: row 1 with first card */}
          <div className="mt-12">
            <h3 className="text-2xl font-semibold mb-6 text-gray-900">Work Experience</h3>
            {/* row 1 with first card */}
            <div className="grid grid-cols-1 gap-8">
              {personalInfo.workExperience.slice(0, 1).map(w => (
                <div 
                  key={w.id}
                  onClick={() => setSelectedWork(w)}
                  className="bg-gray-50 rounded-lg shadow-md overflow-hidden cursor-pointer transform transition-transform hover:scale-105"
                >
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xl font-medium text-gray-900 whitespace-pre-line">{w.role}</span>
                      <span className="text-lg text-gray-600 whitespace-pre-line">{w.company}</span>
                    </div>
                    <p className="text-gray-600 mb-4">{w.period}</p>
                    <ul className="list-disc list-outside pl-6 text-gray-700">
                      {w.bullets.map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            {/* Row 2 with remaining cards (2 per row) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
              {personalInfo.workExperience.slice(1).map(w => (
                <div 
                  key={w.id} 
                  onClick={() => setSelectedWork(w)}
                  className="bg-gray-50 rounded-lg shadow-md overflow-hidden cursor-pointer transform transition-transform hover:scale-105"
                >
                  <div className="p-6">
                    <div className="flex flex-wrap justify-between items-center mb-2">
                      <span className="text-xl font-medium text-gray-900 whitespace-pre-line">{w.role}</span>
                      <span className="text-lg lg:text-xl text-gray-600 whitespace-pre-line">{w.company}</span>
                    </div>
                    <p className="text-gray-600 mb-4">{w.period}</p>
                    <ul className="list-disc list-outside pl-6 text-gray-700">
                      {w.bullets.map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>


      {/* Modal for experience details */}
      {selectedEducation && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-20"
          onClick={() => setSelectedEducation(null)}
        >
          <div
            className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold">{selectedEducation.degree}</h3>
              <button
                onClick={() => setSelectedEducation(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            <p className="text-gray-600 mb-2"><strong>School:</strong> {selectedEducation.institution}</p>
            <p className="text-gray-600 mb-2"><strong>Major:</strong> {selectedEducation.major}</p>
            <p className="text-gray-600 mb-2"><strong>Period:</strong> {selectedEducation.period}</p>
            <p className="text-gray-700 mb-4">{selectedEducation.details}</p>
            <div className="text-gray-700 mb-2"><strong>Focus:</strong> {selectedEducation.focus}</div>
            <div className="text-gray-700 mb-2"><strong>Core Courses:</strong> {selectedEducation.courses}</div>
            <div className="text-gray-700"><strong>Thesis:</strong> {selectedEducation.thesis}</div>
          </div>
        </div>
      )}
      {selectedWork && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-20"
          onClick={() => setSelectedWork(null)}
        >
          <div
            className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold">{selectedWork.role}</h3>
              <button onClick={() => setSelectedWork(null)} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">&times;</button>
            </div>
            <p className="text-gray-600 mb-2"><strong>Company:</strong> {selectedWork.company}</p>
            <p className="text-gray-600 mb-2"><strong>Period:</strong> {selectedWork.period}</p>
            <ul className="list-disc list-inside text-gray-700 mb-4">
              {selectedWork.details.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
            {selectedWork.honors && (
              <div className="mt-6">
                <h4 className="text-xl font-semibold mb-4">Honors & Awards</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {selectedWork.honors.map(h => (
                    <div key={h.id} className="bg-gray-50 rounded p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-gray-900">{h.title} ({h.year})</span>
                      </div>
                      <p className="text-gray-700"><strong>{h.name}:</strong> {h.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <section ref={contactRef} className="scroll-mt-24 py-16 bg-amber-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold mb-8 text-gray-900">Contact</h2>
          <p className="text-gray-700 text-lg mb-2">
            <strong>Email:</strong>{' '}
            <a href={`mailto:${personalInfo.email}`} className="hover:underline">{personalInfo.email}</a>
          </p>
          <p className="text-gray-700 text-lg mb-2">
            <strong>LinkedIn:</strong>{' '}
            <a href={personalInfo.linkedin} target="_blank" rel="noopener noreferrer" className="hover:underline">
              linkedin.com/in/xinwen-xu-077671112
            </a>
          </p>
          <p className="text-gray-700 text-lg"><strong>Location:</strong> {personalInfo.location}</p>
        </div>
      </section>

      <footer className="py-8 bg-amber-50 text-center">
        <p className="text-gray-600 text-sm">© {new Date().getFullYear()} Xu Xinwen. All rights reserved.</p>
      </footer>
    </div>
  );
}
